import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Input, Label, ListBox, ListBoxItem, SearchField, Select, TextField } from '@heroui/react';

import { SkeletonCardColumns } from '../components/common/Skeleton';
import { EmptyResult } from '../components/common/LoadMore';
import Pagination from '../components/common/Pagination';
import Notice from '../components/common/Notice';
import TimeAgo from '../components/common/TimeAgo';
import FundingFilters from '../components/common/FundingFilters';
import FundingSummary from '../components/common/FundingSummary';
import { TagChip } from '../components/common/StatusChip';
import { useBrowseState, useSearchTerm } from '../components/common/useBrowseState';
import userService from '../services/auth.service';
import { isFiltering, SORTS } from '../services/browseState';
import { messageOf, SORT_OPTIONS, SELECT_PLACEHOLDER, RESULT_COLLECTIONS } from '../i18n/el';

// ΛΑ-14: αναζήτηση πόρων **σε όλες τις συλλογές που βλέπει ο χρήστης**.
//
// Δική της οθόνη και όχι πεδίο μέσα στη σελίδα συλλογής, γιατί απαντά σε άλλο
// ερώτημα. Η σελίδα συλλογής απαντά στο «τι έχει αυτή η ομάδα»· εδώ το ερώτημα
// είναι «πού έχω ξαναδεί αυτή την πρόσκληση», και η απάντηση μπορεί να είναι σε
// συλλογή που ο χρήστης δεν σκέφτηκε να ανοίξει. Η **ΛΑ-15** το επιβεβαιώνει:
// ονομάζει τη θεματική συλλογή ως **φίλτρο**, και φίλτρο πάνω σε πεδίο που είναι
// ήδη σταθερό δεν είναι φίλτρο.
//
// Το εύρος το ορίζει **ο διακομιστής**: μόνο πόροι σε συλλογές ορατές στον
// αιτούντα. Η οθόνη δεν κρύβει τίποτα και δεν έχει τρόπο να κρύψει — ό,τι
// φτάσει εδώ επιτρέπεται να φανεί.

/**
 * Μία γραμμή αποτελέσματος.
 *
 * Οδηγεί στη σελίδα της συλλογής με ανοιχτό τον πόρο — τον **μόνιμο σύνδεσμο**
 * — και όχι σε δική της οθόνη λεπτομέρειας. Ο πόρος έχει νόημα μέσα σε συλλογή:
 * εκεί ζουν τα σχόλια, οι αξιολογήσεις και τα καθήκοντα, και εκεί ο χρήστης
 * μπορεί να ενεργήσει. Δεύτερη οθόνη λεπτομέρειας θα ήταν δεύτερη υλοποίηση της
 * ίδιας κάρτας, που θα απέκλινε.
 */
function ResultRow({ resource, collectionsById }) {
    // ⚠ **Β1: ΟΛΕΣ ΟΙ ΟΡΑΤΕΣ ΣΥΛΛΟΓΕΣ, ΟΧΙ Η ΠΡΩΤΗ.**
    //
    // Ο τίτλος οδηγούσε στη `collections[0]`, δηλαδή στη συλλογή που **έτυχε** να
    // είναι πρώτη στο έγγραφο — και η σειρά είναι η σειρά **καταχώρησης**, όχι
    // κάτι που ο χρήστης γνωρίζει ή επέλεξε. Για κοινό πόρο (D9) καταχωρημένο
    // πρώτα σε ιδιωτική ομάδα και προσαρτημένο μετά σε δημόσια, το αποτέλεσμα
    // της αναζήτησης άνοιγε την **ιδιωτική**.
    //
    // Δεν ήταν διαρροή —ο εξυπηρετητής φιλτράρει το `collections[]` ανά
    // αναγνώστη— αλλά ήταν **προορισμός που κανείς δεν διάλεξε**: ο χρήστης
    // ζήτησε «πού έχω ξαναδεί αυτή την πρόσκληση» και έπαιρνε μία απάντηση από
    // τις πολλές, αυθαίρετα.
    //
    // Ο τίτλος **παύει να είναι σύνδεσμος**: ένα όνομα δεν μπορεί να δείχνει σε
    // δύο μέρη. Οι προορισμοί γίνονται **ορατοί και αριθμημένοι**, ένας ανά
    // ορατή συλλογή, και ο χρήστης διαλέγει.
    const links = (resource.collections ?? [])
        .map((id) => collectionsById.get(String(id)))
        .filter(Boolean);

    return (
        <li className="flex flex-col gap-2 border-b border-border py-4 last:border-b-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-lg font-semibold">{resource.title}</span>
                <span className="text-sm text-muted">
                    <TimeAgo date={resource.updatedAt} />
                </span>
            </div>

            {resource.description && (
                <p className="line-clamp-2 text-sm text-muted">{resource.description}</p>
            )}
            <FundingSummary funding={resource.funding} compact />

            {/* ⚠ **ΕΜΦΑΝΙΖΕΤΑΙ ΚΑΙ ΟΤΑΝ ΕΙΝΑΙ ΚΕΝΟ, ΜΕ ΡΗΤΟ ΜΗΝΥΜΑ.** Κενή σειρά
                συνδέσμων δεν διαβάζεται ως «καμία ορατή συλλογή» — διαβάζεται ως
                «δεν φόρτωσε». Η περίπτωση είναι σπάνια αλλά υπαρκτή: ο πόρος
                αποσυνδέθηκε από την τελευταία ορατή συλλογή ανάμεσα στα δύο
                αιτήματα.

                Η εκφώνηση λέει **«που βλέπετε»** και όχι «όλες»: ο κοινός πόρος
                μπορεί να ζει και αλλού, και ο αριθμός θα διαβαζόταν ως πλήρης. */}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                <span className="text-muted">
                    <i className="fa-solid fa-layer-group me-1.5" aria-hidden="true" />
                    {RESULT_COLLECTIONS.label}:
                </span>
                {links.length === 0 ? (
                    <span className="text-muted">{RESULT_COLLECTIONS.none}</span>
                ) : (
                    links.map((one) => (
                        <Link
                            key={one.id}
                            to={`/collections/${one.id}?resource=${resource.id}`}
                            title={RESULT_COLLECTIONS.open(one.title)}
                            className="font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                        >
                            {one.title}
                        </Link>
                    ))
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-sm text-muted underline underline-offset-4"
                >
                    {resource.url}
                </a>
                {resource.tags?.map((tag) => (
                    <TagChip key={tag} appearance="outline">{tag}</TagChip>
                ))}
            </div>

        </li>
    );
}

export default function SearchPage() {
    const { state, query, setFilter, setPage, setSort } = useBrowseState({
        sorts: SORTS.resources
    });
    const [term, setTerm] = useSearchTerm(
        state.q,
        useCallback((value) => setFilter({ q: value }, { replace: true }), [setFilter])
    );

    const [results, setResults] = useState([]);
    const [meta, setMeta] = useState(null);

    // ⚠ **Β1: ΤΑ ΟΝΟΜΑΤΑ ΕΡΧΟΝΤΑΙ ΣΤΟ `meta` ΤΗΣ ΙΔΙΑΣ ΑΠΟΚΡΙΣΗΣ**, όχι από
    // δεύτερη κλήση. Το πρότυπο είναι της **D29**: δεύτερο αίτημα θα σήμαινε ότι
    // ο πελάτης ενώνει δύο εύρη ορατότητας, δηλαδή **τρίτη διατύπωση** του
    // κανόνα — και ο κανόνας ορατότητας είναι ακριβώς εκείνος που δεν επιτρέπεται
    // να έχει δεύτερη ευκαιρία να αποκλίνει.
    //
    // Ο χάρτης χτίζεται κατά την απόδοση και όχι σε κατάσταση: είναι **παράγωγο**
    // του `meta`, και δεύτερη κατάσταση για κάτι που υπολογίζεται είναι δεύτερη
    // πηγή αλήθειας που θα αποσυγχρονιστεί.
    const collectionsById = new Map(
        (meta?.collections ?? []).map((one) => [String(one.id), one])
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setLoading(true);

        userService.searchResources(query).then(
            ({ data, meta: pageMeta }) => {
                setResults(data);
                setMeta(pageMeta);
                setLoading(false);
                setError('');
            },
            (apiError) => {
                setError(messageOf(apiError));
                setLoading(false);
            }
        );
    }, [query]);

    return (
        <div className="flex flex-col gap-6">
            <div>
                <h1 className="text-2xl font-bold">Αναζήτηση πόρων</h1>
                <p className="text-muted">
                    Σε όλες τις συλλογές στις οποίες έχετε πρόσβαση.
                </p>
            </div>

            <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                <SearchField
                    value={term}
                    onChange={setTerm}
                    className="min-w-0 flex-1"
                    aria-label="Αναζήτηση πόρων σε όλες τις συλλογές"
                >
                    <Label>Αναζήτηση</Label>
                    <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input placeholder="Τίτλος, περιγραφή, διεύθυνση ή ετικέτα" />
                        <SearchField.ClearButton />
                    </SearchField.Group>
                </SearchField>

                <Select placeholder={SELECT_PLACEHOLDER}
                    selectedKey={`${state.sort}:${state.order}`}
                    onSelectionChange={(key) => setSort(String(key))}
                    className="w-full lg:w-56"
                >
                    <Label>Ταξινόμηση</Label>
                    <Select.Trigger>
                        <Select.Value />
                        <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                        <ListBox items={SORT_OPTIONS.resources}>
                            {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                        </ListBox>
                    </Select.Popover>
                </Select>
            </div>

            <FundingFilters state={state} setFilter={setFilter} />

            {/* ΛΑ-15: ημερομηνία καταχώρησης. Δύο πεδία και όχι επιλογέας εύρους:
                το `<input type="date">` του περιηγητή είναι ήδη τοπικοποιημένο και
                προσβάσιμο με πληκτρολόγιο, ενώ ένας δικός μας επιλογέας θα ήταν
                τρίτο συστατικό ημερομηνίας στο έργο. */}
            <div className="flex flex-wrap items-end gap-3">
                <TextField
                    value={state.from}
                    onChange={(value) => setFilter({ from: value })}
                    className="w-44"
                >
                    <Label>Από</Label>
                    <Input type="date" />
                </TextField>

                <TextField
                    value={state.to}
                    onChange={(value) => setFilter({ to: value })}
                    className="w-44"
                >
                    <Label>Έως</Label>
                    <Input type="date" />
                </TextField>

                {isFiltering(state) && (
                    <button
                        type="button"
                        onClick={() => setFilter({
                            q: '', tags: [], from: '', to: '', createdBy: '',
                            agency: '', field: '', deadlineBefore: '', minAmount: '', eligible: '',
                            relevance: ''
                        })}
                        className="pb-2 text-sm text-muted underline underline-offset-4"
                    >
                        Καθαρισμός φίλτρων
                    </button>
                )}
            </div>

            {error && <Notice status="danger" title="Η αναζήτηση απέτυχε">{error}</Notice>}

            {loading && <SkeletonCardColumns count={4} />}

            {!loading && !error && results.length === 0 && (
                <EmptyResult
                    isSearching={isFiltering(state)}
                    emptyMessage="Γράψτε έναν όρο για να ξεκινήσει η αναζήτηση."
                />
            )}

            {!loading && results.length > 0 && (
                <ul className="flex flex-col">
                    {results.map((resource) => (
                        <ResultRow
                            key={resource.id}
                            resource={resource}
                            collectionsById={collectionsById}
                        />
                    ))}
                </ul>
            )}

            {!loading && <Pagination meta={meta} onPage={setPage} noun="πόροι" />}
        </div>
    );
}
